<template>
    <Menu as="div" class="relative inline-block text-left">
        <MenuButton class="language-picker-button">
            <GlobeAltIcon class="h-5 w-5" aria-hidden="true" />
            <span class="language-code">{{ currentLocale.toUpperCase() }}</span>
            <ChevronDownIcon class="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" aria-hidden="true" />
        </MenuButton>

        <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
        >
            <MenuItems class="language-picker-menu">
                <div class="py-1">
                    <MenuItem v-slot="{ active }">
                        <button
                            :class="[
                                active ? 'bg-accent/10 text-accent' : 'text-foreground',
                                currentLocale === 'en' ? 'font-semibold' : '',
                                'language-picker-item'
                            ]"
                            @click="changeLocale('en')"
                        >
                            <span class="flag-icon">🇬🇧</span>
                            English
                            <CheckIcon v-if="currentLocale === 'en'" class="h-4 w-4 ml-auto text-accent-600" aria-hidden="true" />
                        </button>
                    </MenuItem>
                    <MenuItem v-slot="{ active }">
                        <button
                            :class="[
                                active ? 'bg-accent/10 text-accent' : 'text-foreground',
                                currentLocale === 'lt' ? 'font-semibold' : '',
                                'language-picker-item'
                            ]"
                            @click="changeLocale('lt')"
                        >
                            <span class="flag-icon">🇱🇹</span>
                            Lietuvių
                            <CheckIcon v-if="currentLocale === 'lt'" class="h-4 w-4 ml-auto text-accent-600" aria-hidden="true" />
                        </button>
                    </MenuItem>
                </div>
            </MenuItems>
        </transition>
    </Menu>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import { GlobeAltIcon, ChevronDownIcon, CheckIcon } from '@heroicons/vue/24/outline';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import type { Locale } from '@/i18n';

export default defineComponent({
    name: 'LanguagePicker',
    components: {
        Menu,
        MenuButton,
        MenuItems,
        MenuItem,
        GlobeAltIcon,
        ChevronDownIcon,
        CheckIcon
    },
    setup() {
        const { locale } = useI18n();
        const router = useRouter();
        const route = useRoute();

        const currentLocale = computed(() => locale.value as Locale);

        const changeLocale = async (newLocale: Locale) => {
            // Navigate to the new locale route while preserving hash anchor
            const newPath = `/${newLocale}`;
            const hash = route.hash;
            await router.push({ path: newPath, hash });

            // Update i18n locale
            locale.value = newLocale;
        };

        return {
            currentLocale,
            changeLocale
        };
    }
});
</script>

<style scoped>
.language-picker-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background-color: rgb(var(--bg-surface));
    border: 1px solid rgb(var(--border) / 0.1);
    border-radius: 0.5rem;
    color: rgb(var(--text-primary));
    font-size: 0.875rem;
    font-weight: 500;
    transition: all 0.2s ease;
    cursor: pointer;
}

.language-picker-button:hover {
    background-color: rgb(var(--bg-muted));
    border-color: rgb(var(--accent-500));
}

.language-picker-button:focus-visible {
    outline: none;
    ring: 2px solid var(--accent-500);
    ring-offset: 2px;
}

.language-code {
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.025em;
}

.language-picker-menu {
    position: absolute;
    right: 0;
    margin-top: 0.5rem;
    width: 12rem;
    origin: top-right;
    background-color: rgb(var(--background));
    border: 1px solid rgb(var(--border) / 0.1);
    border-radius: 0.5rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 50;
    overflow: hidden;
}

.language-picker-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 0.625rem 1rem;
    text-align: left;
    font-size: 0.875rem;
    transition: all 0.15s ease;
    cursor: pointer;
}

.flag-icon {
    font-size: 1.25rem;
    line-height: 1;
}

/* Dark mode support (future) */
.dark .language-picker-button {
    background-color: var(--bg-surface);
    border-color: var(--border);
    color: var(--text-primary);
}

.dark .language-picker-menu {
    background-color: var(--bg-surface);
    border-color: var(--border);
}
</style>
